/**
 * The Screener's running deployment budget.
 *
 * WHY THIS EXISTS (2026-09-17, CIO). The "Optional Sizing Budget (USD)" box on the Screener
 * was READ AND DISCARDED. ScreenView sends `market_rank_only: true` / `include_allocation:
 * false` on every run (hard-coded, no toggle), so screener_core sets `budget_left = 0.0` and
 * every allocation loop -- each one gated on `while budget_left > 0` -- is skipped. The typed
 * figure survived only in diagnostics the payload itself marks inert. A number the CIO can type,
 * that is received and then spent on nothing, is the same failure class as a control that fails
 * closed in silence: it reads as load-bearing and is not.
 *
 * The fix is NOT to switch the allocator back on. The page is deliberately a pure market ranker
 * (Screener trust layer v2, c3b4d86) so portfolio-sizing inputs cannot masquerade as screener
 * guardrails, and the CIO sizes every line by hand. So the budget becomes what it always looked
 * like: a running tally of what HE has chosen, against the number HE typed.
 *
 * DELIBERATELY NOT HERE:
 *   - The 40% funded cap. The CIO holds discretion over what to put to work and declined to have
 *     his own dollar budget marked against the cap ("I should have autonomy to pick the dollar
 *     budget"). The cap is not hidden -- it keeps its own tile at the top of the page.
 *   - Any blocking. Going over the budget warns and nothing more. A budget the CIO typed minutes
 *     ago is guidance he set; only a structural rule (the two-contract minimum, which decides
 *     whether a line can EVER be trimmed) may stop a queue.
 *
 * Everything here is arithmetic on choices already made: no ranking, no selection, no sizing.
 */

/** One contract line the CIO has chosen, at the size he typed. */
export type BudgetItem = {
  /** Contracts. Zero or negative means "not sized yet" and contributes nothing. */
  qty: number;
  /**
   * qty x mid x 100, or null when the row has no usable mid. NEVER coerce null to 0 --
   * a row silently worth nothing understates the basket.
   */
  notional: number | null;
};

export type ScreenerBudget = {
  /** What the CIO typed, or null when the box is blank / not a usable number. */
  budget: number | null;
  /** Tickets queued from this page in this session. */
  queuedNotional: number;
  queuedContracts: number;
  queuedLines: number;
  /** Ticked and sized, not yet queued. */
  basketNotional: number;
  basketContracts: number;
  basketLines: number;
  /** queued + basket. */
  committed: number;
  /** budget - committed, or null when no budget was typed. Negative when over. */
  left: number | null;
  over: boolean;
  /** How far past the budget, 0 when inside it or when no budget was typed. */
  overBy: number;
  /**
   * Chosen lines carrying a quantity but NO usable mid. They are real intent that this total
   * cannot price, so they are counted and disclosed rather than dropped in silence.
   */
  unpricedLines: number;
  /** True when there is nothing at all to report -- no budget, nothing queued, nothing ticked. */
  idle: boolean;
};

/** A blank box, "  ", "abc", 0 and negatives all mean "no budget", never a budget of zero. */
export function parseBudget(raw: unknown): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) && raw > 0 ? raw : null;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function tally(items: BudgetItem[]): { notional: number; contracts: number; lines: number; unpriced: number } {
  let notional = 0;
  let contracts = 0;
  let lines = 0;
  let unpriced = 0;
  for (const item of items || []) {
    const qty = Number(item?.qty);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    const value = item?.notional;
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      notional += value;
      contracts += qty;
      lines += 1;
    } else {
      // Sized by the CIO but unpriceable here. Counted as intent, not as dollars.
      unpriced += 1;
    }
  }
  return { notional, contracts, lines, unpriced };
}

/**
 * The running budget line.
 *
 * `queued` is what this session has already sent to the Trading Desk; `basket` is what is ticked
 * and sized but not yet sent. They are kept apart on purpose -- the CIO asked to see committed
 * separately from still-being-chosen -- and both consume the same budget.
 */
export function computeScreenerBudget(input: {
  budgetText?: unknown;
  queued?: BudgetItem[];
  basket?: BudgetItem[];
}): ScreenerBudget {
  const budget = parseBudget(input?.budgetText);
  const q = tally(input?.queued || []);
  const b = tally(input?.basket || []);
  const committed = q.notional + b.notional;
  const left = budget == null ? null : budget - committed;
  const over = left != null && left < 0;

  return {
    budget,
    queuedNotional: q.notional,
    queuedContracts: q.contracts,
    queuedLines: q.lines,
    basketNotional: b.notional,
    basketContracts: b.contracts,
    basketLines: b.lines,
    committed,
    left,
    over,
    overBy: over && left != null ? Math.abs(left) : 0,
    unpricedLines: q.unpriced + b.unpriced,
    idle: budget == null && committed === 0 && q.unpriced + b.unpriced === 0,
  };
}

/**
 * The sentence under the budget box. It must never imply the figure filters, ranks or sizes
 * anything -- that was the original defect.
 */
export function describeBudgetRole(budget: number | null): string {
  return budget == null
    ? "Not used to rank, filter or size anything. Type an amount to track what your ticked contracts add up to against it."
    : "Not used to rank, filter or size anything. It is the running total on the ranked-contracts table below.";
}
